import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { SavedBrandContext } from "contexts/SavedBrand";
import { SavedBrandCollectionsContext } from "contexts/SavedBrandCollections";
import { TrackedCreatorsContext } from "contexts/TrackedCreators";
import { useContext, useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import {
  BentoBrand,
  SavedBrand,
  SavedBrandStatus,
  TrackedCreatorWrapper,
} from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";

import BrandList from "features/Home/Sidebar/FavoritesList/Collections/BrandList";
import MoveMenu from "features/Home/Sidebar/FavoritesList/Collections/MoveMenu";
import NewCollectionDialog from "features/Home/Sidebar/FavoritesList/Collections/NewCollectionDialog";
import ProgressBar from "features/Home/Sidebar/FavoritesList/ProgressBar";
import { fetcherAuth } from "utils/api";
import { trackEvent } from "utils/tracking";

import styles from "../styles";

interface IProps {
  trackedCreatorWrapper: TrackedCreatorWrapper;
}

const PER_PAGE = 20;

export default function Collection({ trackedCreatorWrapper }: IProps) {
  const { getToken } = useAuth();
  const { setAlert } = useContext(AlertContext);
  const { currentOrg } = useContext(OrganizationUserContext);
  const { selectedCollectionId, setSelectedCollectionId } = useContext(
    SavedBrandCollectionsContext,
  );
  const { updateTrackedCreator } = useContext(TrackedCreatorsContext);
  const [loading, setLoading] = useState<boolean>(false);

  const { bulkBrands, setBulkBrands, selectAll, setSelectAll } =
    useContext(SavedBrandContext);

  const [openCollectionDialog, setOpenCollectionDialog] = useState(false);
  const [currentTab, setCurrentTab] = useState<SavedBrandStatus>(
    SavedBrandStatus.unsent,
  );
  const [isBulkMode, setIsBulkMode] = useState(false);
  const [displayedBrands, setDisplayedBrands] = useState<SavedBrand[]>();

  const [paginationRef, isPaginationRefVisible] = useInView({
    rootMargin: "0px 0px",
  });
  const [unsentTotal, setUnsentTotal] = useState<number>(
    trackedCreatorWrapper.unsentCount,
  );
  const [sentTotal, setSentTotal] = useState<number>(
    trackedCreatorWrapper.sentCount,
  );

  const total =
    currentTab === SavedBrandStatus.sent ? sentTotal || 0 : unsentTotal || 0;

  const handleCreateNewList = (bulkMove?: boolean) => {
    trackEvent("Instagram Sidebar - New List Button Clicked", {
      source: bulkMove ? "Move To Menu" : "New List Button",
    });
    setOpenCollectionDialog(true);
  };

  const handleTabChange = (event: CustomEvent, newValue: string) => {
    trackEvent("Instagram Sidebar - Tab Changed", {
      Tab: newValue,
      "Collection ID": trackedCreatorWrapper?.id,
    });
    setCurrentTab(newValue as SavedBrandStatus);
    setBulkBrands([]);
    setSelectAll(false);
    setIsBulkMode(false);
  };

  const toggleBulkMode = () => {
    if (!trackedCreatorWrapper?.id) return;
    trackEvent("Instagram Sidebar - Select Button Clicked", {
      "Collection ID": trackedCreatorWrapper.id,
    });
    if (isBulkMode) {
      setBulkBrands([]);
      setSelectAll(false);
    }
    setIsBulkMode((prev) => !prev);
  };

  const handleSelectAll = (e: CustomEvent) => {
    trackEvent("Instagram Sidebar - Select All Toggled", {
      "Collection ID": trackedCreatorWrapper?.id,
    });
    const { checked } = e.target;
    if (checked) {
      setBulkBrands(displayedBrands?.map((sb) => sb.bentoBrand) || []);
    } else {
      setBulkBrands([]);
    }
    setSelectAll(checked);
  };

  const fetchInstagramMentionCollectionBrands = async (
    refreshList: boolean = false,
  ) => {
    if (!currentOrg?.id) return;

    if (!trackedCreatorWrapper.id) return;

    setLoading(true);

    let cursor = null;
    if (!refreshList) {
      const lastBrand = displayedBrands?.[displayedBrands.length - 1];
      cursor = lastBrand ? [lastBrand.createdAt, lastBrand.bentoBrandId] : null;
    } else {
      setDisplayedBrands(undefined);
    }

    let url = `/api/organization/${currentOrg?.id}/saved-brands?per_page=${PER_PAGE}`;
    url += `&source_instagram_username=${trackedCreatorWrapper.sourceInstagramUsername}`;
    if (cursor) {
      url += `&cursor=${JSON.stringify(cursor)}`;
    }
    if (currentTab === SavedBrandStatus.sent) {
      url += `&sent=${true}`;
    } else {
      url += `&sent=${false}`;
    }

    try {
      const res = await fetcherAuth(getToken, url, "GET");
      if (currentTab === SavedBrandStatus.sent) {
        setSentTotal(res.total);
        const copy = { ...trackedCreatorWrapper };
        copy.sentCount = res.total;
        updateTrackedCreator(copy);
      } else {
        setUnsentTotal(res.total);
        const copy = { ...trackedCreatorWrapper };
        copy.unsentCount = res.total;
        updateTrackedCreator(copy);
      }

      if (!cursor) {
        setDisplayedBrands(res.brands);
      } else {
        setDisplayedBrands((prev) => {
          if (!prev) {
            return res.brands;
          }
          const brandIdSet = new Set(prev.map((sb) => sb.bentoBrandId));
          const newSavedBrands = [...prev];
          res.brands.forEach((sb: SavedBrand) => {
            if (!brandIdSet.has(sb.bentoBrandId)) {
              brandIdSet.add(sb.bentoBrandId);
              newSavedBrands.push(sb);
            }
          });
          return newSavedBrands;
        });
      }
    } catch (error) {
      setAlert(error.message || "Failed to fetch instagram mentions", "error");
    } finally {
      setLoading(false);
    }
  };

  const bulkMoveToSent = async (bentoBrands: BentoBrand[]) => {
    // remove them from displayed brands
    // reset the totals
    const brandIdSet: Set<number> = new Set(
      bentoBrands.map((b: BentoBrand) => b.id),
    );
    setUnsentTotal((prev) => prev - brandIdSet.size);
    setSentTotal((prev) => prev + brandIdSet.size);
    setDisplayedBrands((prev) =>
      prev?.filter((v) => !brandIdSet.has(v.bentoBrandId)),
    );
  };

  const deleteFromCollection = async (bentoBrand: BentoBrand) => {
    if (currentTab === SavedBrandStatus.unsent) {
      setUnsentTotal((prev) => prev - 1);
    } else {
      setSentTotal((prev) => prev - 1);
    }
    setDisplayedBrands((prev) =>
      prev?.filter((v) => bentoBrand.id === v.bentoBrandId),
    );
  };

  useEffect(() => {
    if (trackedCreatorWrapper.id && !loading) {
      fetchInstagramMentionCollectionBrands(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackedCreatorWrapper?.trackedCreator.id, currentTab]);

  useEffect(() => {
    if (
      trackedCreatorWrapper.id &&
      selectedCollectionId !== trackedCreatorWrapper.id
    ) {
      setSelectedCollectionId(trackedCreatorWrapper.id);
    }

    if (trackedCreatorWrapper?.trackedCreator.id) {
      setUnsentTotal(trackedCreatorWrapper.unsentCount);
      setSentTotal(trackedCreatorWrapper.sentCount);
    }

    return () => {
      setSelectedCollectionId(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackedCreatorWrapper?.trackedCreator.id]);

  useEffect(() => {
    if (
      isPaginationRefVisible &&
      trackedCreatorWrapper.id &&
      displayedBrands &&
      displayedBrands.length < total &&
      !loading
    ) {
      fetchInstagramMentionCollectionBrands();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isPaginationRefVisible,
    trackedCreatorWrapper?.trackedCreator.id,
    currentTab,
    displayedBrands?.length,
  ]);

  useEffect(() => {
    if (
      bulkBrands?.length === displayedBrands?.length &&
      !selectAll &&
      bulkBrands?.length > 0
    ) {
      setSelectAll(true);
    } else if (bulkBrands?.length === 0 && selectAll) {
      setSelectAll(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkBrands?.length]);

  if (!trackedCreatorWrapper.id) {
    return (
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ fontStyle: "italic", px: 2 }}
      >
        No brands found for this username!
      </Typography>
    );
  }

  return (
    <>
      <Grid
        container
        sx={styles.tabContainer}
        alignItems="center"
        justifyContent="space-between"
      >
        <Grid item xs={8.5}>
          <Tabs value={currentTab} onChange={handleTabChange}>
            <Tab
              sx={styles.tab}
              value={SavedBrandStatus.unsent}
              label={<>Unsent ({unsentTotal})</>}
            />
            <Tab
              sx={styles.tab}
              value={SavedBrandStatus.sent}
              label={<>Sent ({sentTotal})</>}
            />
          </Tabs>
        </Grid>

        <Grid item xs>
          <Button
            size="small"
            onClick={toggleBulkMode}
            color={isBulkMode ? "primary" : "secondary"}
          >
            Select {bulkBrands?.length > 0 ? `(${bulkBrands?.length})` : ""}
          </Button>
        </Grid>
      </Grid>
      {/* Select All Checkbox */}
      {isBulkMode && (
        <Grid sx={styles.selectAll}>
          <FormControlLabel
            control={
              <Checkbox
                onChange={handleSelectAll}
                checked={selectAll}
                disableRipple
                icon={
                  <Box
                    sx={{ color: "green.main" }}
                    component="i"
                    className="fa-regular fa-square"
                  />
                }
                checkedIcon={
                  <Box
                    component="i"
                    className="fa-solid fa-square-minus"
                    sx={{ color: "green.main" }}
                  />
                }
              />
            }
            label={<Box sx={{ pr: 1 }}>Select All</Box>}
          />
        </Grid>
      )}
      {/* Brand List */}
      {(displayedBrands === undefined || displayedBrands.length > 0) &&
      total !== 0 ? (
        <BrandList
          displayedBrands={displayedBrands?.map((sb) => sb.bentoBrand) || []}
          loading={loading}
          collectionId={trackedCreatorWrapper.id}
          isBulkMode={isBulkMode}
          ref={paginationRef}
          isDefaultCollection={false}
          deleteFromCollection={deleteFromCollection}
        />
      ) : (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontStyle: "italic", mt: 2, px: 2 }}
        >
          You do not have any {currentTab} brands on this list
        </Typography>
      )}
      {/* Bottom Action Bar */}
      <Grid
        container
        justifyContent={isBulkMode ? "flex-end" : "center"}
        sx={styles.footer}
      >
        {isBulkMode ? (
          <MoveMenu
            handleCreateNewList={handleCreateNewList}
            setIsBulkMode={setIsBulkMode}
            currentTab={currentTab}
            forceRefreshList={() => {
              fetchInstagramMentionCollectionBrands(true);
            }}
            moveBrandsInstagramCollection={bulkMoveToSent}
          />
        ) : (
          <ProgressBar />
        )}
      </Grid>

      <NewCollectionDialog
        open={openCollectionDialog}
        handleClose={() => setOpenCollectionDialog(false)}
        isBulkMode={isBulkMode}
        setIsBulkMode={setIsBulkMode}
      />
    </>
  );
}
